import cx from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { Key } from 'w3c-keys';
import { ActionMenu, } from '../../../../action-menu';
import { Icon, IconColor, IconID, IconSize } from '../../../../icon';
import { View } from '../../../../layout/flex-layout/flex-layout';
import { Popover } from '../../../../popover';
import { Typography } from '../../../../typography';
import { ColumnSort } from '../../object-table.interface';
import { extendActionOnClick } from '../../object-table-action.util';
import { addListenerToHeaderCells } from '../column-header-util';
import styles from './title-header.module.scss';
export const TitleHeader = ({ column, }) => {
    const colDef = column.getColDef();
    const titleHeader = {
        ...colDef.headerComponentParams,
    };
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const popoverRef = useRef(null);
    const titleRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    // Function to return focus to the header cell
    const returnFocusToHeader = useCallback(() => {
        var _a, _b, _c, _d;
        // Find the AG Grid header cell that contains this title header
        const parentElement = (_c = (_b = (_a = titleRef === null || titleRef === void 0 ? void 0 : titleRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.parentElement) === null || _c === void 0 ? void 0 : _c.parentElement;
        if (parentElement &&
            ((_d = parentElement.className) === null || _d === void 0 ? void 0 : _d.includes('ag-header-cell'))) {
            // Focus the AG Grid header cell
            parentElement.focus();
        }
    }, []);
    // Extend the onClick functionality to close the popover on click.
    const actions = extendActionOnClick(titleHeader.actions || [], setIsPopoverOpen);
    // Watch for popover state changes to handle focus management
    const prevIsPopoverOpen = useRef(isPopoverOpen);
    React.useEffect(() => {
        // If popover was open and is now closed, return focus to header
        if (prevIsPopoverOpen.current && !isPopoverOpen) {
            setTimeout(() => {
                returnFocusToHeader();
            }, 0);
        }
        prevIsPopoverOpen.current = isPopoverOpen;
    }, [isPopoverOpen, returnFocusToHeader]);
    const hasActionItems = actions.length > 0 && actions[0].items.length > 0;
    const onlySingleActionItem = actions.length === 1 && actions[0].items.length === 1
        ? actions[0].items[0]
        : null;
    const hoverIconWidth = 38; // 38 = 12 padding + 12 padding + 14 icon size
    const headerTitle = (React.createElement(Typography, { variant: "footnote", className: styles.titleHeaderStyles, overrideTag: "div" },
        React.createElement("b", null, titleHeader.title)));
    const sortIconContainer = (React.createElement(Icon, { id: titleHeader.columnSort === ColumnSort.Asc
            ? IconID.ARROW_UP
            : IconID.ARROW_DOWN, color: IconColor.BLUE, size: IconSize.SMALL, className: styles.columnSortArrow }));
    const hoverIconContainer = (React.createElement(Icon, { id: IconID.CHEVRON_DOWN, color: IconColor.BLUE, size: IconSize.SMALL, className: styles.chevronDownIcon }));
    const getComponentToRender = useCallback(() => {
        if (isHovered) {
            // This logic is to check if the title header text
            // is overflowing the container when we hover over it
            // and add the hover icon
            const container = popoverRef.current;
            const titleContainer = titleRef.current;
            let containerOverflow = false;
            if (container && titleContainer) {
                containerOverflow =
                    titleContainer.clientWidth + hoverIconWidth >
                        container.clientWidth;
            }
            else if (!titleContainer) {
                // titleRef is null because the chevron-only branch unmounts the
                // title node — a subsequent render while still hovered must keep
                // showing only the chevron
                containerOverflow = true;
            }
            // if it overflowing then only render hover icon
            if (containerOverflow) {
                return (React.createElement(View, { className: styles.titleHeaderAlign }, hoverIconContainer));
            }
            // else render text and hover icon both
            return (React.createElement("div", { className: styles.innerContainer },
                React.createElement("div", { ref: titleRef },
                    React.createElement(View, { className: styles.titleHeaderView },
                        headerTitle,
                        titleHeader.columnSort && sortIconContainer)),
                hoverIconContainer));
        }
        return (React.createElement("div", { ref: titleRef },
            React.createElement(View, { className: styles.titleHeaderView },
                headerTitle,
                titleHeader.columnSort && sortIconContainer)));
    }, [
        isHovered,
        headerTitle,
        titleHeader.columnSort,
        sortIconContainer,
        hoverIconContainer,
    ]);
    const handleChange = useCallback((event) => {
        setIsHovered(false);
        if (!hasActionItems) {
            return;
        }
        if (onlySingleActionItem &&
            onlySingleActionItem.onClick &&
            onlySingleActionItem.label) {
            onlySingleActionItem.onClick({
                id: onlySingleActionItem.id,
                label: onlySingleActionItem.label,
                event: event,
            });
            if (titleHeader.onHeaderClick)
                titleHeader.onHeaderClick(titleHeader.title);
            return;
        }
        setIsPopoverOpen(!isPopoverOpen);
        // Call the callback when column popover is opened
        const newPopoverState = !isPopoverOpen;
        if (newPopoverState && titleHeader.onHeaderClick) {
            titleHeader.onHeaderClick(titleHeader.title);
        }
    }, [hasActionItems, isPopoverOpen, onlySingleActionItem, titleHeader]);
    React.useEffect(() => {
        var _a, _b, _c;
        const parentElement = (_c = (_b = (_a = titleRef === null || titleRef === void 0 ? void 0 : titleRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.parentElement) === null || _c === void 0 ? void 0 : _c.parentElement;
        addListenerToHeaderCells(parentElement, handleChange);
    }, [handleChange]);
    return (React.createElement(View, { className: cx(styles.titleHeaderContainer, {
            [styles.defaultHoverState]: !hasActionItems,
            [styles.titleHeaderContainerHighlight]: !titleHeader.disableHover,
        }), "data-tooltip-content": titleHeader.toolTip, "data-testid": "title-header-testid", onClick: (event) => {
            handleChange(event);
        }, "aria-label": titleHeader.ariaLabel, onKeyDown: (event) => {
            const { key } = event;
            if (key !== Key.Enter) {
                return;
            }
            handleChange(event);
        } },
        React.createElement("div", { ref: popoverRef, className: styles.titleHeader, onMouseEnter: () => {
                if (hasActionItems)
                    setIsHovered(true);
            }, onMouseLeave: () => {
                if (hasActionItems)
                    setIsHovered(false);
            }, "data-testid": "title-header-inner-container" }, getComponentToRender()),
        React.createElement(Popover, { align: "bottom-start", content: React.createElement(ActionMenu, { show: isPopoverOpen, menuItemGroups: actions, disableMenuTabIndex: true }), targetRef: popoverRef, isVisible: isPopoverOpen, autoFocusOnVisible: true, onClickOutside: () => setIsPopoverOpen(false), onEscape: () => {
                setIsPopoverOpen(false);
            } })));
};
//# sourceMappingURL=title-header.js.map